<script setup lang="ts">
import type { SectionsGoogleMaps } from '@autobid/strapi-integration/rest/components'
import { useCheckUserPermission } from '@autobid/ui/composables/useCheckUserPermission'
import { useOneTrust } from '@autobid/ui/composables/useOneTrust'
import { GOOGLE_DOMAIN_COOKIE_NAME } from '@autobid/ui/constants/GOOGLE_DOMAIN_COOKIE_NAME'
import { GOOGLE_GROUP_COOKIE_NAME } from '@autobid/ui/constants/GOOGLE_GROUP_COOKIE_NAME'
import { googleMapsCva } from '@autobid/ui/utils/cva/googleMapsCva'
import { openOneTrustModal } from '@autobid/ui/utils/openOneTrustModal'
import { computed, inject } from 'vue'

interface Props {
  data: SectionsGoogleMaps
  isEmbeded?: boolean
}
const props = defineProps<Props>()
const { checkUserPermission } = useCheckUserPermission()
const isAdmin = computed(() => checkUserPermission('edit_static'))
const isSidebar: boolean = inject('isSidebar', false)

const url = (function () {
  const regex = /src="([^"]+)"/
  const match = props.data.embededHtml.match(regex)

  if (!match)
    return null

  const embed = match[1]
  const splittedEmbed = embed.split('!')
  const latitude = splittedEmbed[5]?.slice(2)
  const longitude = splittedEmbed[6]?.slice(2)

  if (!latitude || !longitude)
    return null

  const locationId = splittedEmbed[17]?.length > 4 ? splittedEmbed[17] : ''
  const placeName = locationId ? splittedEmbed[18]?.slice(2) : ''
  const ZOOM = 16.8

  const external = locationId
    ? `https://www.google.com/maps/place/${placeName}/@${longitude},${latitude}/data=!4m6!3m5!${locationId}!8m2!3d${longitude}!4d${latitude}!16s//`
    : `https://www.google.com/maps/@${longitude},${latitude},${ZOOM}z`

  return { embed, external }
})()

const { optanonConsent } = useOneTrust()
const isAllowed = computed(() => {
  return (
    optanonConsent.value?.includes(`${GOOGLE_GROUP_COOKIE_NAME}:1`)
    || optanonConsent.value?.includes(`${GOOGLE_DOMAIN_COOKIE_NAME}:1`)
  )
})
</script>

<template>
  <div
    v-if="url"
    class="flex w-full"
    :class="[
      googleMapsCva({ width: isEmbeded ? data.width : 'w-full', alignment: data.alignment?.name }),
      isSidebar && 'px-3 lg:px-2',
      !isSidebar && 'px-3 xl:pl-0',
    ]"
  >
    <div
      class="w-full"
      :class="googleMapsCva({ width: isEmbeded ? 'w-full' : data.width, alignment: data.alignment?.name })"
    >
      <p v-if="!isAllowed" class="mb-2 block">
        <i18n-t keypath="google-maps.accept-cookies" tag="p">
          <template #openInNewTab>
            <common-link
              :href="url.external"
              class="underline"
              rel="nofollow noopener noreferrer"
            >
              {{ $t('google-maps.open-in-new-tab') }}
            </common-link>
          </template>

          <template #googleCookiesLink>
            <common-link href="#" class="underline" @click="openOneTrustModal">
              {{ $t('google-maps.google-cookies') }}
            </common-link>
          </template>
        </i18n-t>
      </p>

      <iframe
        v-if="isAllowed"
        class="w-full"
        :class="googleMapsCva({
          aspectRatio: data.aspectRatio || 'ratio-4:3',
          desktopHeight: data.desktopHeight,
        })"
        frameborder="0"
        :src="url.embed"
      />

      <a
        v-else
        :href="url.external"
        target="_blank"
        rel="nofollow noopener noreferrer"
      >
        <CommonStrapiImage
          v-if="data.replacementImage"
          :data="data.replacementImage"
        />

        <img v-else src="./map-placeholder.webp" alt="map">
      </a>
    </div>
  </div>
  <p v-else-if="isAdmin" class="py-3 text-2xl text-red">
    GoogleMapsComponent:
    {{ $t('google-maps.invalid-embeded-html-parameter') }}
  </p>
</template>
